import React, { useEffect, useState } from 'react';
import { WorkforceEmployeeListView, WorkspaceOption } from '../../components/workforce/WorkforceEmployeeListView';
import { EmployeeDetailView } from '../../components/workforce/EmployeeDetailView';
import { api as apiClient } from '../../lib/api';
import { Activity, ShieldCheck } from 'lucide-react';

export const PlatformWorkforceOverviewPage: React.FC = () => {
  const [workspaces, setWorkspaces] = useState<WorkspaceOption[]>([]);
  const [selectedWorkspaceId, setSelectedWorkspaceId] = useState<string>('');
  const [selectedEmployeeId, setSelectedEmployeeId] = useState<string | null>(null);
  const [selectedDateStr, setSelectedDateStr] = useState<string>(new Date().toISOString().split('T')[0] || '');
  const [loading, setLoading] = useState<boolean>(true);

  useEffect(() => {
    const fetchWorkspaces = async () => {
      try {
        setLoading(true);
        const data = await apiClient.platform.getWorkspaces(0, 100);
        if (data.content && data.content.length > 0) {
          const mapped: WorkspaceOption[] = data.content.map((ws: any) => ({
            id: ws.id,
            name: ws.name,
            ownerName: ws.ownerName || ws.owner?.displayName || 'Workspace Admin'
          }));
          setWorkspaces(mapped);
          if (mapped[0]?.id) {
            setSelectedWorkspaceId(mapped[0].id);
          }
        }
      } catch (err) {
        console.error('Failed to fetch platform workspaces:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchWorkspaces();
  }, []);

  return (
    <div className="p-6 space-y-6 max-w-7xl mx-auto min-h-screen">
      {/* Platform Level Header */}
      <div className="flex items-center justify-between border-b border-slate-200 pb-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
            <Activity className="w-6 h-6 text-indigo-600" />
            Super Admin Workforce Activity Intelligence
          </h1>
          <p className="text-sm text-slate-500 mt-1">
            Real telemetry monitoring, native foreground app detection, browser domain tracking, and employee activity timelines.
          </p>
        </div>

        <div className="flex items-center gap-2 bg-indigo-50 border border-indigo-100 px-3.5 py-1.5 rounded-lg text-xs text-indigo-700">
          <ShieldCheck className="w-4 h-4 text-indigo-600" />
          <span>Zero Keylogging • Privacy Guaranteed</span>
        </div>
      </div>

      {/* View Switcher */}
      {loading ? (
        <div className="p-12 text-center text-slate-400 bg-slate-900/50 border border-slate-800 rounded-xl">
          Loading Super Admin workforce context...
        </div>
      ) : selectedEmployeeId ? (
        <EmployeeDetailView
          workspaceId={selectedWorkspaceId}
          userId={selectedEmployeeId}
          dateStr={selectedDateStr}
          onBack={() => setSelectedEmployeeId(null)}
        />
      ) : (
        <WorkforceEmployeeListView
          workspaces={workspaces}
          selectedWorkspaceId={selectedWorkspaceId}
          onSelectWorkspace={(wsId) => {
            setSelectedWorkspaceId(wsId);
            setSelectedEmployeeId(null);
          }}
          onSelectEmployee={(userId, dateStr) => {
            if (dateStr) setSelectedDateStr(dateStr);
            setSelectedEmployeeId(userId);
          }}
        />
      )}
    </div>
  );
};

export default PlatformWorkforceOverviewPage;
